import React from 'react';
import { MdSettings, MdClear } from 'react-icons/md';

import { ButtonSecondary, MeatballMenu, ValidationLevel, ValidationProps } from 'ui';
import { Tag } from 'app/components/tag';
import { isDisabled } from 'app/infrastructure/data/date';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Favourite } from 'app/area/favourite/favourite';
import { FavouriteInstance } from 'app/area/favourite/favourite-context';
import { toUpdateSavedReport } from 'app/area/shared/navigation';

import { createDefaultSavedReport } from 'app/area/shared/saved-reports/utils/create-default-saved-report';
import { SavedReport, SavedReportValidation } from 'app/area/shared/saved-reports/types-store';
import { DashboardAdminTranslation } from '../../constants';
import { api } from '../api';
import { dashboardLayoutTypeContext } from '../constants';

import styles from './saved-reports-favourite.scss';

interface SavedReportsFavouriteProps {
  savedReport: SavedReport;

  containerWidth: number;
  prevElementSequence: number;
  nextElementSequence: number;

  editSavedReport: (savedReport: SavedReport) => void;
  updateSavedReport: (currentSavedReportId: number, savedReport: SavedReport, validation: SavedReportValidation) => void;
  deleteSavedReport: (savedReportId: number) => void;

  requestCustomWidth: (id: number, value: number) => void;

  display: boolean;

  validation: SavedReportValidation;
  translation: DashboardAdminTranslation;
}

// tslint:disable-next-line: max-func-body-length
export const SavedReportsFavourite: React.FC<SavedReportsFavouriteProps> = ({
  savedReport, containerWidth, prevElementSequence, nextElementSequence,
  editSavedReport, updateSavedReport, deleteSavedReport,
  requestCustomWidth, display,
  validation, translation
}) => {

  const onEditReport = () => {
    toUpdateSavedReport(savedReport.code?.value, true);
  };

  const onEdit = () => {
    editSavedReport(savedReport);
  };

  const onAdd = () => {
    editSavedReport(createDefaultSavedReport(undefined, savedReport.sequence ?? 0));
  };

  const onMoveUp = () => {
    updateSavedReport(savedReport.code?.value, { ...savedReport, sequence: prevElementSequence }, validation);
  };

  const onMoveDown = () => {
    updateSavedReport(savedReport.code?.value, { ...savedReport, sequence: nextElementSequence }, validation);
  };

  const onRemove = () => {
    deleteSavedReport(savedReport.code?.value);
  };

  const containerStyle = {
    width: (containerWidth ?? 0) > 0 ?
      `${containerWidth}%` :
      '0',
    ...(!display && { display: 'none' })
  };

  const hasError = Object.keys(validation ?? {})
    .some(key => (validation[key]?.messages?.length > 0) && validation[key]?.level === ValidationLevel.error);

  const hasWarnings = Object.keys(validation ?? {})
    .some(key => (validation[key]?.messages?.length > 0) && validation[key]?.level === ValidationLevel.warning);

  const validationClass = hasError
    ? styles.dashboardCellError
    : (hasWarnings ? styles.dashboardCellWarning : styles.dashboardCell);

  return <div className={styles.dashboardWrapper} style={containerStyle}>
    <div className={validationClass}>
      <div className={styles.headerWrapper}>
        <h3>{savedReport.code?.description}</h3>
        {isDisabled(savedReport.disabled)
          ? <Tag className={styles.disabledTag}>
            {translation.disabled}
          </Tag>
          : null}
        <div className={styles.toolButton}>
          <ButtonSecondary onClick={onEdit}>
            <MdSettings size={20} title={translation.edit_connection} />
          </ButtonSecondary>
        </div>
        <div className={styles.toolButton}>
          <ButtonSecondary onClick={onRemove}>
            <MdClear size={20} title={translation.remove} />
          </ButtonSecondary>
        </div>
        <div className={styles.meatballMenu}>
          <MeatballMenu closeAfterClick={true}>
            {!!savedReport.editable && <li>
              <button className="dropdown-item"
                onClick={onEditReport}>
                {translation.edit_report}
              </button>
            </li>}
            <li>
              <button className="dropdown-item"
                onClick={onAdd}>
                {translation.add_report}
              </button>
            </li>
            {isDefinedIncludingZero(prevElementSequence) && <li>
              <button className="dropdown-item"
                onClick={onMoveUp}>
                {translation.move_up}
              </button>
            </li>}
            {isDefinedIncludingZero(nextElementSequence) && <li>
              <button className="dropdown-item"
                onClick={onMoveDown}>
                {translation.move_down}
              </button>
            </li>}
          </MeatballMenu>
        </div>
      </div>
      {(hasError || hasWarnings)
        ? <div className={styles.favouriteError}>
          <div className={styles.warningIcon} />
          {Object.keys(validation ?? {})
            .map(key =>
              (validation[key] as ValidationProps)?.messages
                ?.map(message => message?.message)
                ?.filter(text => text)
                ?.join(' '))
            .join(' ')
          }
        </div>
        : <div className={styles.favouriteWrapper}>
          <FavouriteInstance
            hideFilters={true}
            hideHeader={true}
            hideVisualizationOptions={true}
            reportApiOverrides={api}
            layoutTypeContextId={dashboardLayoutTypeContext}
            height={savedReport?.height}>
            <Favourite
              favouriteId={savedReport?.code?.value}
              overrideReport={savedReport?.selectedReport}
              embeddedFavourite={true}
              statisticalReportExternalProps={{
                requestCustomWidth,
                reflowHandle: containerStyle.width
              }} />
          </FavouriteInstance>
        </div>}
    </div>
  </div>;
};
